import React, { useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FaRegHeart, FaHeart } from 'react-icons/fa';
import { defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import HeartIcon from '~/icons/components/HeartIcon';
import PressableIconButton from '~/shared/components/PressableIconButton';
import {
  useAccountActions,
  useWishlistIds,
} from '~/techstyle-shared/react-accounts';
import {
  GridColorSwatches,
  useProductContext,
  useTabIndex,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';

import { trackWishlist } from '../../actions';

/**
 * Variant Overview:
 *
 *  1. Default (ie, original) variant:
 *     Icon - FaHeart / FaRegHeart (react-icons, 12px)
 *     Animation - CSSTransition pulseOutAnimation on click
 *     Click model - pointer-down/up on DefaultToggleButton, disabled during animation
 *     Interaction State Management - Pending state lock; frozenIsInWishlist locks state during CSSTransition
 *
 * 2. "large-white-heart" (ie, isLargeWhiteHeart) variant:
 *     Icon - HeartIcon (mask-based, 24px)
 *     Animation - PressableIconButton (shared spring scale + PressRing)
 *     Click model - onClick on PressableIconButton, guarded by isLoading
 *     Interaction State Management - Optimistic UI; frozenIsInWishlist set immediately, rolled back on failure
 *
 */

const pulseOutAnimation = keyframes`
from {
    opacity: 1;
    transform: scale3d(1, 1);
}

to {
  opacity: 0;
  transform: scale3d(2, 2, 1);
}
`;

// TODO
// update bg color of icon to use theme tokens when SxF mocks available
// leverage iconColorWishlist token when default variant is deprecated
const DefaultToggleButton = styled.button`
  border: none;
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  position: relative;
  transform-origin: center center;
  background-color: ${({ theme }) => theme.colors.iconColorWishlistButtonBg};
  border-radius: 50%;
  padding: 0.5em;

  &.enter-active {
    animation-name: ${pulseOutAnimation};
    animation-duration: ${({ animationDuration }) => animationDuration}ms;
  }

  ${({ toggleButtonStyle }) => toggleButtonStyle};
`;

const baseHeartIconStyle = css`
  width: 12px;
  height: auto;
  color: ${({ theme }) => theme.colors.iconColorWishlist};
  ${({ heartIconStyle }) => heartIconStyle};
`;

const EmptySmallHeart = styled(FaRegHeart)`
  ${baseHeartIconStyle}
  ${({ $heartIconStyle }) => $heartIconStyle && $heartIconStyle}
`;

const FilledSmallHeart = styled(FaHeart)`
  ${baseHeartIconStyle}
  ${({ $heartIconStyle }) => $heartIconStyle && $heartIconStyle}
`;

const LargeHeartToggleButton = styled(PressableIconButton)`
  width: 40px;
  height: 40px;
  -webkit-tap-highlight-color: transparent;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    bottom: -4px;
    left: -4px;
    right: -4px;
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => theme.colors.iconColorWishlistFocusOutline};
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

const intlAriaLabel = defineMessages({
  removeFromWishlist: {
    id: 'site_product_page.added_to_wishlist',
    defaultMessage: 'added to wishlist',
  },
  addToWishlist: {
    id: 'site_product_page.add_to_wishlist',
    defaultMessage: 'add to wishlist',
  },
});

function WishlistDefaultToggleButton({
  animationDuration = 700,
  autoTagPrefix = 'product',
  heartIconStyle,
  product: productFromProps,
  onClick,
  tabIndex,
  toggleButtonStyle,
  variant = 'default',
  ...rest
}) {
  // default only
  const [isAnimating, setAnimating] = useState(false);
  const [defaultIsPressed, setDefaultIsPressed] = useState(false);

  // shared variants
  const [isLoading, setLoading] = useState(false);
  const [frozenIsInWishlist, setFrozenIsInWishlist] = useState(null);

  const intl = useIntl();
  const actions = useAccountActions();
  const { hasProductId, networkStatus } = useWishlistIds();
  const productFromContext = useProductContext();
  const product = productFromProps || productFromContext;
  const { masterProductId } = product;
  tabIndex = useTabIndex(tabIndex);

  const dispatch = useDispatch();

  const isLargeWhiteHeart = variant === 'large-white-heart';

  const realIsInWishlist = hasProductId(masterProductId);

  useEffect(() => {
    if (!isAnimating && !isLoading && networkStatus.isUpToDate) {
      setFrozenIsInWishlist(null);
    }
  }, [isAnimating, isLoading, networkStatus.isUpToDate]);

  // default only
  const startAnimation = useCallback(() => {
    setFrozenIsInWishlist(realIsInWishlist);
    setAnimating(true);
    setLoading(true);
  }, [realIsInWishlist]);

  const addToWishlist = useCallback(async () => {
    if (isLargeWhiteHeart) {
      setFrozenIsInWishlist(true);
      setLoading(true);
    } else {
      startAnimation();
    }
    try {
      await actions.addWishlistItem(masterProductId);
    } catch (error) {
      if (isLargeWhiteHeart) {
        // Roll back to the real server-known state, not the optimistic value
        setFrozenIsInWishlist(realIsInWishlist);
      }
      // TODO: replace no error w/ Sentry
    } finally {
      setLoading(false);
    }
  }, [
    isLargeWhiteHeart,
    startAnimation,
    actions,
    masterProductId,
    realIsInWishlist,
  ]);

  const removeFromWishlist = useCallback(async () => {
    if (isLargeWhiteHeart) {
      setFrozenIsInWishlist(false);
      setLoading(true);
    } else {
      startAnimation();
    }
    try {
      await actions.deleteWishlistItem(masterProductId);
    } catch (error) {
      if (isLargeWhiteHeart) {
        // Roll back to the real server-known state, not the optimistic value
        setFrozenIsInWishlist(realIsInWishlist);
      }
      // TODO: replace with Sentry
    } finally {
      setLoading(false);
    }
  }, [
    isLargeWhiteHeart,
    startAnimation,
    actions,
    masterProductId,
    realIsInWishlist,
  ]);

  // default only — CSSTransition enter complete
  const handleOnEntered = useCallback(() => {
    setAnimating(false);
    setFrozenIsInWishlist((prevState) => !prevState);
  }, []);

  const handleClick = useCallback(() => {
    if (isLoading) {
      return;
    }
    if (!onClick) {
      if (realIsInWishlist) {
        removeFromWishlist();
      } else {
        addToWishlist();
        dispatch(
          trackWishlist({
            product_id_hit: masterProductId.toString(),
            name: product.label,
            variant:
              GridColorSwatches.getSwatchItem(product).color ||
              product.swatchLabel,
          })
        );
      }
    } else {
      onClick({
        removeFromWishlist,
        addToWishlist,
        realIsInWishlist,
      });
    }
  }, [
    isLoading,
    onClick,
    removeFromWishlist,
    addToWishlist,
    realIsInWishlist,
    masterProductId,
    dispatch,
    product,
  ]);

  if (!masterProductId) {
    throw new Error(
      `WishlistDefaultToggleButton must be used with a valid Product (specified via the 'product' prop or via ProductContext)`
    );
  }

  const isInWishlist =
    frozenIsInWishlist != null ? frozenIsInWishlist : realIsInWishlist;

  const ariaLabel = isInWishlist
    ? intl.formatMessage(intlAriaLabel.removeFromWishlist)
    : intl.formatMessage(intlAriaLabel.addToWishlist);

  const autoTag = isInWishlist
    ? `${autoTagPrefix}-favorite-icon-filled`
    : `${autoTagPrefix}-favorite-icon-empty`;

  // large-white-heart variant
  if (isLargeWhiteHeart) {
    return (
      <LargeHeartToggleButton
        aria-label={ariaLabel}
        aria-pressed={isInWishlist}
        onClick={handleClick}
        tabIndex={tabIndex}
        data-autotag={autoTag}
        {...rest}
      >
        <HeartIcon isSelected={isInWishlist} />
      </LargeHeartToggleButton>
    );
  }

  // default variant
  const renderIcon = () =>
    isInWishlist ? (
      <FilledSmallHeart $heartIconStyle={heartIconStyle} />
    ) : (
      <EmptySmallHeart $heartIconStyle={heartIconStyle} />
    );

  return (
    <CSSTransition
      in={isAnimating}
      exit={false}
      timeout={animationDuration}
      onEntered={handleOnEntered}
    >
      <DefaultToggleButton
        type="button"
        animationDuration={animationDuration}
        aria-label={ariaLabel}
        aria-pressed={isInWishlist}
        disabled={isAnimating}
        onPointerDown={() => setDefaultIsPressed(true)}
        onPointerUp={() => {
          defaultIsPressed && handleClick();
          setDefaultIsPressed(false);
        }}
        onPointerLeave={() => setDefaultIsPressed(false)}
        tabIndex={tabIndex}
        toggleButtonStyle={toggleButtonStyle}
        data-autotag={autoTag}
        {...rest}
      >
        {renderIcon()}
      </DefaultToggleButton>
    </CSSTransition>
  );
}

WishlistDefaultToggleButton.propTypes = {
  animationDuration: PropTypes.number,
  autoTagPrefix: PropTypes.string,
  heartIconStyle: PropTypes.any,
  onClick: PropTypes.func,
  product: PropTypes.object,
  tabIndex: PropTypes.number,
  toggleButtonStyle: PropTypes.any,
  variant: PropTypes.oneOf(['default', 'large-white-heart']),
};

export default WishlistDefaultToggleButton;
